import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from '../../core/services/user.service';
import { ConfirmationService } from '../../core/services/confirmation.service';
import { passwordValidator } from '../../core/validators/password.validator';

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  standalone: false
})
export class ResetPasswordComponent implements OnInit {
  resetForm: FormGroup;
  loading = false;
  showPassword = false;
  showConfirmPassword = false;
  formSubmitted = false;
  resetError = false;

  /** Hidden by default — shown only when the token itself is invalid/expired (backend said so) */
  invalidLink = false;

  /**
   * True when validating the token failed for a reason OTHER than the token
   * being invalid — rate-limited (429), network error, or a server error.
   * Shown with a "Try again" action instead of "Request new reset link",
   * since the token itself may still be perfectly valid.
   */
  tokenCheckUnavailable = false;
  tokenCheckUnavailableReason: 'throttled' | 'network' | 'server' = 'server';

  /** True while the token validation API call is in-flight */
  tokenChecking = true;

  /** Translation key for the message shown under the submit button on failure */
  resetErrorKey = 'AUTH.RESET_PASSWORD.FAILED';

  token = '';
  email = '';   // resolved from token by the API — pre-fills the forgot-password form

  private isSubmitting = false;
  private translate = inject(TranslateService);
  private destroyRef = inject(DestroyRef);

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private confirmationService: ConfirmationService
  ) {
    this.resetForm = this.fb.group({
      password: ['', [Validators.required, passwordValidator()]],
      password_confirmation: ['', [Validators.required]]
    }, { validators: this.passwordMatchValidator });
  }

  ngOnInit(): void {
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.token = params['token'] || '';

      if (!this.token) {
        // No token in URL at all — immediately show the invalid state
        this.tokenChecking = false;
        this.invalidLink = true;
        return;
      }

      this.validateToken();
    });
  }

  /**
   * Classifies an HTTP error into why it happened, shared by validateToken()
   * and onSubmit() so the 429/network/5xx thresholds live in exactly one
   * place instead of being duplicated (and risking drifting out of sync).
   * 'client' means the backend explicitly rejected the request (e.g. 400/404
   * invalid token) — everything else is a transient failure that says
   * nothing about whether the token itself is valid.
   */
  private classifyError(error: { status?: number }): 'throttled' | 'network' | 'server' | 'client' {
    if (error?.status === 429) return 'throttled';
    if (!error?.status) return 'network'; // status 0 (or missing) — request never reached the server
    if (error.status >= 500) return 'server';
    return 'client';
  }

  /**
   * Calls the backend to verify the token is still valid and resolves the email.
   * On success → show the reset form.
   * On failure → the reason matters: an actual invalid/expired token (400)
   * shows the resend state, but a rate limit (429), network error, or server
   * error (5xx) does NOT mean the token is bad — those show a distinct
   * "try again" state instead, so we don't send a perfectly valid link's
   * owner off to request a brand new one.
   */
  private validateToken(): void {
    this.tokenChecking = true;
    this.invalidLink = false;
    this.tokenCheckUnavailable = false;

    this.userService.validateResetToken(this.token)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.tokenChecking = false;
          this.email = res.email;
        },
        error: (error: { status?: number }) => {
          this.tokenChecking = false;

          const reason = this.classifyError(error);
          if (reason === 'client') {
            this.invalidLink = true;
          } else {
            this.tokenCheckUnavailable = true;
            this.tokenCheckUnavailableReason = reason;
          }
        }
      });
  }

  passwordMatchValidator(form: FormGroup): { mismatch: boolean } | null {
    const password = form.get('password')?.value;
    const confirmPassword = form.get('password_confirmation')?.value;
    return password === confirmPassword ? null : { mismatch: true };
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  onSubmit(): void {
    if (this.isSubmitting) return;

    this.formSubmitted = true;

    if (this.resetForm.invalid) {
      this.resetForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    this.loading = true;
    this.resetError = false; // clear any error left over from a previous attempt
    const formValue = this.resetForm.value;

    this.userService.resetPassword({
      token: this.token,
      password: formValue.password,
      password_confirmation: formValue.password_confirmation
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.loading = false;
          this.isSubmitting = false;
          this.confirmationService.swalSuccess(
            this.translate.instant('AUTH.RESET_PASSWORD.SUCCESS')
          );
          this.router.navigate(['/account/login']);
        },
        error: (error: { status?: number }) => {
          this.loading = false;
          this.isSubmitting = false;
          this.resetError = true;

          const reason = this.classifyError(error);
          this.resetErrorKey = reason === 'throttled'
            ? 'AUTH.RESET_PASSWORD.TOO_MANY_ATTEMPTS'
            : reason === 'network'
              ? 'AUTH.RESET_PASSWORD.CONNECTION_ERROR'
              : 'AUTH.RESET_PASSWORD.FAILED';
        }
      });
  }

  /**
   * Redirects to the forgot-password page.
   * Pre-fills the email field if it was resolved from the (now-expired) token.
   */
  goToForgotPassword(): void {
    this.router.navigate(['/account/forgot-password'], {
      queryParams: this.email ? { email: this.email } : {}
    });
  }

  /**
   * Re-runs token validation from the "try again" state (rate limit /
   * network / server error) — NOT the same as goToForgotPassword(), since
   * the token itself may still be valid here.
   */
  retryTokenValidation(): void {
    this.validateToken();
  }
}
